import axios from 'axios';
import { toast } from 'react-toastify';
import { getAccessToken } from '../auth/apiClient';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';

// Create axios instance for general API calls
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 30000,
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
    async (config) => {
        try {
            const token = await getAccessToken();
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
        } catch (error) {
            // Public routes still run. Protected routes return 401.
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for global error handling
apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Handle 401 Unauthorized
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            // Try to refresh token if available
            try {
                const refreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');

                if (refreshToken) {
                    const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
                        refreshToken
                    });

                    const { accessToken } = response.data;

                    const storage = localStorage.getItem('rememberMe') === 'true' ? localStorage : sessionStorage;
                    storage.setItem('accessToken', accessToken);

                    // Retry original request
                    originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                    return apiClient(originalRequest);
                }
            } catch (refreshError) {
                // Refresh failed, redirect to login
                localStorage.clear();
                sessionStorage.clear();

                if (window.location.pathname !== '/login') {
                    toast.error('Session expired. Please login again.');
                    window.location.href = '/login';
                }

                return Promise.reject(refreshError);
            }
        }

        // Handle other errors
        if (error.response) {
            const status = error.response.status;
            const message = error.response.data?.message || error.message;

            // Don't show toast for 401 (handled above)
            if (status !== 401) {
                if (status === 403) {
                    toast.error('You do not have permission to perform this action');
                } else if (status === 404) {
                    toast.error('Resource not found');
                } else if (status === 429) {
                    toast.error('Too many requests. Please slow down.');
                } else if (status >= 500) {
                    toast.error('Server error. Please try again later.');
                } else if (message) {
                    toast.error(message);
                }
            }
        } else if (error.request) {
            toast.error('Network error. Please check your connection.');
        }

        return Promise.reject(error);
    }
);

export default apiClient;
